import { toast } from "react-toastify";

/**
 * Trigger a native browser download for the given URL + filename.
 *
 * Creates a transient <a download href> and programmatically clicks it.
 * The browser then handles the entire transfer in its native download
 * manager — real progress (KB/s, MB loaded, ETA) appears in the browser
 * bar, like OneDrive / Drive / Gmail downloads.
 *
 * Pair with a `toast.info("... — preparing download")` at the call site
 * so the user sees instant feedback while the (typically tiny) signed-URL
 * POST is in flight.
 *
 * See `docs/downloads/DOWNLOADS_UX_ARCHITECTURE.md` for the
 * broader architecture (signed temporary URLs, why native, etc.).
 *
 * @param {string} url        Full URL (signed). e.g. `${API_URL}/api/files/Hexagonal_Data.7z?expires=...&user=...&sig=...`
 * @param {string} filename   Filename hint for the browser. Server's
 *                            Content-Disposition takes precedence if set.
 *                            ALSO used to gate the Edge SmartScreen warning —
 *                            only `.7z` files trip SmartScreen hangs (`.zip` is
 *                            a high-reputation format and passes through cleanly).
 * @param {number} [sizeBytes] Optional. File size in bytes — used only to decide
 *                            whether to show the Edge SmartScreen warning toast.
 *                            Threshold: 100 MB AND filename ends in `.7z`. Omit
 *                            for unknown-size downloads — warning is suppressed.
 */
export function triggerNativeDownload(url, filename, sizeBytes) {
  maybeWarnEdgeUser(filename, sizeBytes);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  // Some browsers (Safari) require the element to be in the DOM before click.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// Microsoft Defender SmartScreen in Edge can hang large-file downloads
// indefinitely in `Unconfirmed XXX.crdownload` state — bytes arrive, but
// Edge refuses to rename the file to its final name pending a verification
// that never completes for low-reputation domains. Confirmed 2026-06-26 on
// the dev server (HTTPS, real domain, same-origin). Affects Edge only;
// Chrome and Firefox are unaffected.
//
// We can't fix this client-side (SmartScreen runs on the file regardless
// of JS API), but we can warn affected users once per session so they
// know what to try if their download appears stuck.
//
// See: hsrc-api/context/backlog/2026-06-26 - Edge SmartScreen blocks
// large .7z finalisation — submit URL reputation.md
// Per-trigger sessionStorage key so each distinct download (filename) gets
// its own once-per-session toast. Users who download multiple large files
// see the warning for each one — useful because the message sets expectations
// about THAT specific download (size + likely wait), not a generic "Edge has
// issues" banner.
const EDGE_WARN_SESSION_KEY_PREFIX = "stx-edge-smartscreen-warned:";
const EDGE_WARN_THRESHOLD_BYTES = 100 * 1024 * 1024; // 100 MB — SmartScreen rarely hangs on small files

function maybeWarnEdgeUser(filename, sizeBytes) {
  // Only warn for files large enough to plausibly trip SmartScreen verification.
  // Unknown size (sizeBytes undefined/null) -> assume small, don't warn.
  if (!sizeBytes || sizeBytes < EDGE_WARN_THRESHOLD_BYTES) return;

  // Format gate: SmartScreen specifically delays `.7z` archives — they're
  // less common in business contexts and historically over-represented in
  // malware, so Edge runs deeper verification on them. `.zip` files have
  // built up enough Microsoft reputation that they pass through cleanly
  // even at 350+ MB. Verified 2026-06-26 by user testing a 350 MB .zip
  // alongside .7z files of the same/smaller size in Edge.
  if (!filename || !/\.7z$/i.test(filename)) return;

  if (typeof navigator === "undefined" || typeof window === "undefined") return;

  const isEdge = /Edg\//.test(navigator.userAgent);
  if (!isEdge) return;

  const key = EDGE_WARN_SESSION_KEY_PREFIX + (filename || "_unnamed");
  try {
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, "1");
  } catch (_) {
    // sessionStorage can throw in some privacy modes — fall through and toast anyway.
  }

  toast.warn(
    "Edge may pause large .7z downloads while Microsoft Defender SmartScreen " +
      "verifies the file. If your download appears stuck at " +
      "\"Unconfirmed XXX.crdownload\" with 0 B/s, either wait a further " +
      "2–5 minutes, or download the file using Chrome or Firefox which " +
      "do not experience the same delay.",
    { autoClose: 10000 },
  );
}
